import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { BaseType } from '../../../types/Enums';
import { IssueComponentCategoryInput } from '../../../types/issues';
import { translate } from '../../../types/EnumTranslations';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { RELEVANT_BASE_TYPES } from '../../../Helpers/baseTypeUtils';

interface ComponentCategoryPickerProps {
    value: IssueComponentCategoryInput | null;
    onChange: (value: IssueComponentCategoryInput) => void;
    disabled?: boolean;
}

const NO_SPECIFIC_TYPE = '';

/**
 * Two selects — base type, then an optional specific type from the type
 * config — for categorising an issue that has no actual component to link.
 * Dumb: holds no state of its own and reports every change to the caller.
 */
const ComponentCategoryPicker: React.FC<ComponentCategoryPickerProps> = ({ value, onChange, disabled }) => {
    const { types, getTypesForBaseType, getTranslation } = useBuildComponentTypes();

    const baseType = value?.baseType;

    // Specific types of the chosen base type, sorted by name. Utility and
    // outdoor types are grouped by their category so the list stays scannable.
    const specificTypeGroups = useMemo(() => {
        if (!baseType) return [];
        const keys = getTypesForBaseType(baseType);
        const byGroup = new Map<string, Array<{ key: string; name: string }>>();
        keys.forEach(key => {
            const group = (baseType === BaseType.Utility || baseType === BaseType.Outdoor)
                ? (types[key]?.category ? translate(types[key].category!) : 'Övrigt')
                : '';
            const list = byGroup.get(group) ?? [];
            list.push({ key, name: getTranslation(key, baseType) });
            byGroup.set(group, list);
        });
        return Array.from(byGroup.entries())
            .map(([group, items]) => ({
                group,
                items: items.sort((a, b) => a.name.localeCompare(b.name, 'sv'))
            }))
            .sort((a, b) => a.group.localeCompare(b.group, 'sv'));
    }, [baseType, getTypesForBaseType, getTranslation, types]);

    const handleBaseTypeChange = (next: string) => {
        if (!next) return;
        onChange({ baseType: next as BaseType });
    };

    const handleSpecificTypeChange = (next: string) => {
        if (!baseType) return;
        onChange(next === NO_SPECIFIC_TYPE ? { baseType } : { baseType, specificType: next });
    };

    return (
        <div className="d-flex flex-column flex-md-row gap-2">
            <Form.Select
                size="sm"
                value={baseType ?? ''}
                onChange={(e) => handleBaseTypeChange(e.target.value)}
                disabled={disabled}
                aria-label="Bastyp"
            >
                <option value="" disabled>Välj bastyp…</option>
                {RELEVANT_BASE_TYPES.map(type => (
                    <option key={type} value={type}>{translate(type)}</option>
                ))}
            </Form.Select>
            <Form.Select
                size="sm"
                value={value?.specificType ?? NO_SPECIFIC_TYPE}
                onChange={(e) => handleSpecificTypeChange(e.target.value)}
                disabled={disabled || !baseType}
                aria-label="Komponenttyp"
            >
                <option value={NO_SPECIFIC_TYPE}>— Ingen specifik typ —</option>
                {specificTypeGroups.map(({ group, items }) => group ? (
                    <optgroup key={group} label={group}>
                        {items.map(item => (
                            <option key={item.key} value={item.key}>{item.name}</option>
                        ))}
                    </optgroup>
                ) : (
                    items.map(item => (
                        <option key={item.key} value={item.key}>{item.name}</option>
                    ))
                ))}
            </Form.Select>
        </div>
    );
};

export default ComponentCategoryPicker;
